import {
    memo,
    useCallback,
    type ComponentType,
    type FC,
    type FunctionComponent,
    type MutableRefObject
} from "react";
import {
    DataGridPremium,
    DataGridPremiumProps,
    GRID_ACTIONS_COLUMN_TYPE,
    GridCallbackDetails,
    GridCellParams,
    GridColumnOrderChangeParams,
    GridColumnHeaderParams,
    GridColumns,
    GridColumnVisibilityModel,
    GridEventListener,
    GridExperimentalPremiumFeatures,
    GridInputSelectionModel,
    GridPinnedColumns,
    GridRowIdGetter,
    GridRowParams,
    GridSelectionModel,
    GridSortModel,
    GridValidRowModel,
    useGridApiRef,
    GridSlotsComponent,
    GridRowClassNameParams,
    GridRenderCellParams,
    GridSlotsComponentsProps
} from '@mui/x-data-grid-premium';
import LinearProgress from '@mui/material/LinearProgress';
import type { CardProps } from '@mui/material/Card';
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useMui } from "hooks";
import {
    DetailContentProps,
    EnabledTablePlugins,
    PaginationArgs,
    TablePlugin
} from "ui/molecules/TableGrids/types";
import type { PaginateResourceResponse } from "types";

// import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

import NoDataOverlay from './NoDataOverlay';
import Toolbar from "./Toolbar";
import usePlugins from "./Plugins";
import type { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { CopyToClipboardSettings } from "./Plugins/useCopyToClipboard";
import Layout, { type LayoutProps } from "./Layout";
import { TableGridMode } from "consts/table";

type Props = {
    readonly mode: TableGridMode;
    readonly columns: GridColumns;
    readonly data: PaginateResourceResponse<any>['data']['data'];
    readonly currentPage: number;
    readonly perPage: number;
    readonly total: number;
    readonly isLoading: boolean;
    readonly DetailPanelContent?: FunctionComponent<DetailContentProps>;
    readonly onPageChange: (parimationParams: PaginationArgs) => void;
    readonly onSortModelChange: (
        sortModel: GridSortModel,
        details: GridCallbackDetails<any>) => void;
    readonly sortModel: GridSortModel;
    readonly columnVisibilityModel: GridColumnVisibilityModel;
    readonly onColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
    readonly onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
    readonly getRowId: GridRowIdGetter<any>;
    readonly checkboxSelection?: boolean;
    readonly disableSelectionOnClick?: boolean;
    readonly pinnedColumns?: GridPinnedColumns;
    readonly CardProps?: CardProps;
    readonly onRowClick?: GridEventListener<"rowClick">;
    readonly plugins?: EnabledTablePlugins;
    readonly initialState?: GridInitialStatePremium;
    readonly isRowSelectable?: (params: GridRowParams) => boolean;
    readonly selectionModel?: GridInputSelectionModel;
    readonly onSelectionModelChange?: (selectionModel: GridSelectionModel, details: GridCallbackDetails<any>) => void;
    readonly isCellEditable?: (params: GridCellParams) => boolean;
    readonly experimentalFeatures?: Partial<GridExperimentalPremiumFeatures>;
    readonly processRowUpdate?: <T extends GridValidRowModel>(newRow: T, oldRow: T) => any;
    readonly onCellEditStop?: GridEventListener<"cellEditStop">;
    readonly tableApiRef?: MutableRefObject<GridApiPremium | undefined>;
    readonly Layout?: ComponentType<LayoutProps>;
    readonly LayoutProps?: LayoutProps;
    readonly components?: Partial<GridSlotsComponent>;
    readonly componentsProps?: GridSlotsComponentsProps;
    readonly rowsPerPageOptions?: Array<number>;
    readonly getRowClassName?: (params: GridRowClassNameParams<GridValidRowModel>) => string;
};

const Table: FC<Props> = ({
    mode,
    columns,
    data,
    checkboxSelection,
    disableSelectionOnClick,
    currentPage,
    perPage,
    total,
    isLoading,
    DetailPanelContent,
    sortModel,
    columnVisibilityModel,
    onPageChange,
    onSortModelChange,
    onColumnOrderChange,
    onColumnVisibilityModelChange,
    getRowId,
    onRowClick,
    plugins,
    initialState,
    pinnedColumns,
    isRowSelectable,
    selectionModel,
    onSelectionModelChange,
    isCellEditable,
    experimentalFeatures,
    processRowUpdate,
    onCellEditStop,
    tableApiRef,
    components,
    componentsProps,
    rowsPerPageOptions,
    getRowClassName,
    CardProps = {},
    LayoutProps = {},
    Layout: LayoutComponent = Layout
}) => {
    useMui({
        timeout: 50
    });

    const apiRef = useGridApiRef();

    if (tableApiRef) {
        tableApiRef.current = apiRef.current;
    }

    const getDetailPanelContent: DataGridPremiumProps['getDetailPanelContent'] =
        DetailPanelContent
            ? ({ row }: DetailContentProps) => (
                <DetailPanelContent row={row} />
            )
            : undefined;

    const getDetailPanelHeight: DataGridPremiumProps['getDetailPanelHeight'] =
        useCallback(() => 'auto' as const, []);

    const handlePageChange = (newPage: number) => {
        onPageChange({ page: newPage + 1, perPage });
    };

    const handlePageSizeChange = (perPage: number) => {
        onPageChange({ page: 1, perPage });
    };

    const getExperimentalFeatures: () => Partial<GridExperimentalPremiumFeatures> | undefined = () => {
        if (!experimentalFeatures && isCellEditable) {
            return {
                newEditingApi: true
            };
        }

        return experimentalFeatures;
    };

    usePlugins(apiRef, plugins!);

    const isOpenBankingMode = [
        TableGridMode.OpenBankingBankingSDK,
        TableGridMode.OpenBankingIbanXS,
        TableGridMode.OpenBankingBankingSDKRecipients,
        TableGridMode.OpenBankingBankingSDKConnectorsRecipientsPivot,
        TableGridMode.OpenBankingIbanXSBanksRecipientsPivot,
        TableGridMode.OpenBankingIbanXSBanksCountriesPivot
    ].includes(mode);

    const normalizeDataTestPart = (value: unknown) => String(value ?? '')
        .replace(/\s+/g, '')
        .replace(/[^\w.-]/g, '_');

    const dataTestIdPrefix = 'openBanking.table';

    const columnsWithDataTestIds = !isOpenBankingMode
        ? columns
        : columns.map(column => {
            const field = normalizeDataTestPart(column.field);

            if (column.type === GRID_ACTIONS_COLUMN_TYPE || field === GRID_ACTIONS_COLUMN_TYPE) {
                return column;
            }

            const renderCell = column.renderCell;
            const renderHeader = column.renderHeader;

            return {
                ...column,
                renderCell: (params: GridRenderCellParams) => {
                    const rowId = normalizeDataTestPart(params.row?.coreId ?? params.id);

                    return (
                        <Box data-test-id={`${dataTestIdPrefix}.row.${rowId}.cell.${field}`}>
                            {renderCell
                                ? renderCell(params)
                                : (params.formattedValue ?? params.value ?? '-')}
                        </Box>
                    );
                },
                renderHeader: (params: GridColumnHeaderParams) => (
                    <Tooltip
                        title={params.colDef.headerName ?? params.field}
                        arrow
                        placement='top'
                    >
                        <Box
                            data-test-id={`${dataTestIdPrefix}.column.${field}.header`}
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            {renderHeader
                                ? renderHeader(params)
                                : (params.colDef.headerName ?? params.field)}
                        </Box>
                    </Tooltip>
                )
            };
        });

    return (
        <LayoutComponent
            isCellEditable={Boolean(isCellEditable)}
            CardProps={CardProps}
            {...LayoutProps}
        >
            <DataGridPremium
                apiRef={apiRef}
                data-test-id={isOpenBankingMode ? `${dataTestIdPrefix}.grid` : undefined}
                sx={isOpenBankingMode ? {
                    '& .MuiDataGrid-columnHeader': {
                        fontWeight: 700
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700
                    },
                    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                        fontWeight: 700
                    }
                } : undefined}
                pagination
                checkboxSelection={checkboxSelection}
                disableSelectionOnClick={disableSelectionOnClick}
                hideFooterSelectedRowCount
                rows={data}
                getRowId={getRowId}
                loading={isLoading}
                columns={columnsWithDataTestIds}
                componentsProps={{
                    ...componentsProps,
                    row: {
                        'data-test-id': 'transactions.table.row',
                        ...componentsProps?.row
                    }
                }}
                components={{
                    Toolbar,
                    NoRowsOverlay: NoDataOverlay,
                    LoadingOverlay: LinearProgress,
                    ...components
                }}
                density='compact'
                paginationMode="server"
                sortingMode="server"
                page={Math.max(0, currentPage - 1)}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSortModelChange={onSortModelChange}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                onColumnOrderChange={onColumnOrderChange}
                sortModel={sortModel}
                pinnedColumns={pinnedColumns}
                columnVisibilityModel={columnVisibilityModel}
                pageSize={perPage}
                rowsPerPageOptions={rowsPerPageOptions}
                rowCount={total}
                getDetailPanelHeight={getDetailPanelHeight}
                getDetailPanelContent={getDetailPanelContent}
                onRowClick={onRowClick}
                initialState={initialState}
                isRowSelectable={isRowSelectable}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
                isCellEditable={isCellEditable}
                experimentalFeatures={getExperimentalFeatures()}
                processRowUpdate={processRowUpdate}
                onCellEditStop={onCellEditStop}
                getRowClassName={getRowClassName}
            />
        </LayoutComponent>
    );
};

Table.defaultProps = {
    CardProps: {
        sx: {}
    },
    plugins: {
        [TablePlugin.CopyToClipboard]: {
            isEnabled: true,
            ignoreFields: ['__', 'actions']
        } as CopyToClipboardSettings
    }
};

export default memo(Table);
